import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { CommonService } from 'src/app/shared/common.service';
import { IMailboxSettings, IProviderLabel } from '../../../models/settings.models';
import { SettingsService } from '../../../services/settings.services';

// ─────────────────────────────────────────────────────────────────────────────
// ProviderLabelQueueComponent
// ─────────────────────────────────────────────────────────────────────────────
// Labels a connected mailbox already carries, waiting on a decision.
//
// These are NOT labels yet, and the distinction is the point: while a row is
// pending there is no label behind it, so it cannot be filtered on or used in an
// automation. Importing one is what creates the label and makes it usable. The
// copy says so, because "pending" on its own reads like a delay rather than a
// deliberate holding state.
//
// Gmail can be enumerated on demand — hence the Discover button, offered per
// connected account. Outlook cannot: Microsoft exposes no way to list
// categories, so those appear here on their own as categorised mail arrives.
// That asymmetry is real and is explained in the template rather than hidden.
// ─────────────────────────────────────────────────────────────────────────────

const MAILBOX_PAGE_SIZE = 100;

@Component({
  selector: 'app-provider-label-queue',
  templateUrl: './provider-label-queue.component.html',
  styleUrls: ['./provider-label-queue.component.scss'],
})
export class ProviderLabelQueueComponent implements OnInit {

  /**
   * Every mailbox, so Discover can be offered per connected Gmail account.
   * Loaded here rather than taken from the Connections tab, whose list is one page.
   */
  mailboxes: IMailboxSettings[] = [];

  /** A label was imported, so the list above this needs refetching. */
  @Output() imported = new EventEmitter<void>();

  pending: IProviderLabel[] = [];
  loading = false;
  /** The last read failed, so an empty list means "unknown", not "nothing waiting". */
  loadFailed = false;
  /** Ids currently being decided, so their row can show it and not double-fire. */
  deciding = new Set<number>();
  discovering: number | null = null;

  constructor(
    private settingsService: SettingsService,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadMailboxes();
  }

  private loadMailboxes(): void {
    this.settingsService.listInboxMailboxes({ limit: MAILBOX_PAGE_SIZE, page: 1 }).pipe(
      switchMap((first) => {
        const pages = first.metadata?.pages ?? 1;
        const rest = Array.from({ length: Math.max(pages - 1, 0) }, (_unused, index) =>
          this.settingsService.listInboxMailboxes({ limit: MAILBOX_PAGE_SIZE, page: index + 2 }));
        return (rest.length ? forkJoin(rest) : of([])).pipe(
          map((others) => [first, ...others].flatMap((response) => response.data ?? [])));
      }),
    ).subscribe({
      next: (mailboxes) => { this.mailboxes = mailboxes; },
      error: () => { this.mailboxes = []; },
    });
  }

  /**
   * Gmail accounts we can actually enumerate right now.
   *
   * CONNECTED as well as Gmail: Discover calls the provider, so offering it on
   * a mailbox whose connection is lost or expired is a button that can only
   * fail. Two of the accounts on this tenant are in exactly that state.
   */
  get gmailMailboxes(): IMailboxSettings[] {
    return (this.mailboxes || []).filter(
      (mailbox) => (mailbox.provider || '').toLowerCase() === 'gmail'
        && mailbox.connection_status === 'connected');
  }

  get isEmpty(): boolean {
    return !this.loading && !this.loadFailed && this.pending.length === 0;
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.settingsService.listProviderLabels().subscribe({
      next: (response) => {
        this.pending = response.data ?? [];
        this.loading = false;
      },
      // Cleared here as well as in `next` rather than in `complete`: an errored
      // observable never completes, and the flag would strand the spinner. The
      // interceptor already toasts the failure.
      error: () => {
        this.pending = [];
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  discover(mailboxId: number): void {
    if (this.discovering !== null) {
      return;
    }
    this.discovering = mailboxId;
    const known = new Set(this.pending.filter((ref) => ref.mailbox_id === mailboxId).map((ref) => ref.id));
    const email = this.mailboxes.find((mailbox) => mailbox.id === mailboxId)?.email ?? '';
    this.settingsService.discoverProviderLabels(mailboxId).subscribe({
      next: (response) => {
        this.discovering = null;
        // Without this, finding nothing new looks exactly like the button doing nothing.
        const found = (response.data ?? []).filter((ref) => !known.has(ref.id)).length;
        this.commonService.showToast(
          found
            ? this.translate.instant('settings.provider_labels.discover_found', { count: found, email })
            : this.translate.instant('settings.provider_labels.discover_none', { email }),
          found ? 'SUCCESS' : 'INFO',
        );
        // Refetched rather than taking the response: discover returns only that
        // mailbox's rows, and this list spans every account.
        this.load();
      },
      error: () => { this.discovering = null; },
    });
  }

  accept(ref: IProviderLabel): void {
    this.decide(ref, 'accept');
  }

  reject(ref: IProviderLabel): void {
    this.decide(ref, 'reject');
  }

  trackByRefId(_index: number, ref: IProviderLabel): number {
    return ref.id;
  }

  private decide(ref: IProviderLabel, decision: 'accept' | 'reject'): void {
    if (this.deciding.has(ref.id)) {
      return;
    }
    this.deciding.add(ref.id);

    this.settingsService.decideProviderLabels([ref.id], decision).subscribe({
      next: () => {
        this.deciding.delete(ref.id);
        // Dropped locally rather than refetched: either decision takes the row
        // out of the queue, and a round trip would blink the whole list.
        this.pending = this.pending.filter((row) => row.id !== ref.id);
        if (decision === 'accept') {
          this.imported.emit();
        }
      },
      error: () => { this.deciding.delete(ref.id); },
    });
  }
}
